import API from "@/api";

let ws = null;
const connect = store => {
  if (ws) return;
  const url = process.env.VUE_APP_API.replace("http", "ws") + "/chat";
  ws = new WebSocket(url);

  ws.onopen = () => {
    console.log("connected to chat");
    ws.send(store.rootState.user.token.value);
    store.dispatch("getChatGroups");
  };

  ws.onmessage = e => {
    const { type, data } = JSON.parse(e.data);

    if (type === "GROUP_CREATED") {
      store.commit("ADD_GROUP", data);
    } else if (type === "GROUP_NAME_UPDATED") {
      store.commit("UPDATE_GROUP_NAME", data);
    } else if (type === "MESSAGE") {
      store.commit("ADD_MESSAGE", data);
    } else if (type === "MESSAGE_DELETED") {
      store.dispatch("deleteMessage", data);
    }
  };

  ws.onclose = e => {
    console.log("disconnected from chat");
    ws = null;
    if (e.code === 3333) return;
    setTimeout(() => connect(store), 5000);
  };
};

const emptyState = () => ({
  attachmentConfig: {
    loaded: false,
    blacklist: [],
    maxNameLength: 0,
    maxSize: 0
  },
  groups: [],
  messages: [],
  loading: false,
  page: 0,
  selectedId: null,
  totalMessages: 0,
  unread: 0
});

const state = emptyState();

const getters = {
  selectedGroup: state => state.groups.find(g => g.id === state.selectedId),

  unread: state => {
    if (!state.groups.length) return state.unread;
    return state.groups.reduce((acc, g) => {
      if (!g.last_message) return acc;
      const last_read_at = new Date(g.last_read_at);
      const last_message_at = new Date(g.last_message.created_at);
      if (last_read_at < last_message_at) acc++;
      return acc;
    }, 0);
  },

  userList: (state, _, rootState, rootGetters) => id => {
    const group = state.groups.find(g => g.id === id);
    const getNickname = rootGetters["contacts/getNickname"];
    return group.users
      .filter(u => u.id !== rootState.user.id)
      .map(u => getNickname(u))
      .sort()
      .join(", ");
  }
};

const mutations = {
  ADD_GROUP(state, group) {
    state.groups.push(group);
  },

  ADD_MESSAGE(state, message) {
    if (state.selectedId === message.group_id) {
      state.messages.push(message);
      state.totalMessages++;
    }

    const group = state.groups.find(g => g.id === message.group_id);
    if (group) group.last_message = message;
  },

  DELETE_MESSAGE(state, { group_id, message_id, true_delete }) {
    if (state.selectedId !== group_id) return;

    if (true_delete) {
      state.messages = state.messages.filter(m => m.id !== message_id);
    } else {
      const message = state.messages.find(m => m.id === message_id);
      if (message) message.message = null;
    }
  },

  RESET(state) {
    Object.assign(state, emptyState());
  },

  SET_ATTACHMENT_CONFIG(state, config) {
    state.attachmentConfig.loaded = true;
    state.attachmentConfig.blacklist = config.blacklist;
    state.attachmentConfig.maxNameLength = config.max_name_length;
    state.attachmentConfig.maxSize = config.max_size;
  },

  SET_GROUPS(state, group) {
    state.groups = group;
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_MESSAGES(state, messages) {
    state.messages = messages;
  },

  SET_PAGE(state, page) {
    state.page = page;
  },

  SET_SELECTED_ID(state, id) {
    state.selectedId = id;
  },

  SET_TOTAL_MESSAGES(state, total) {
    state.totalMessages = total;
  },

  SET_UNREAD(state, unread) {
    state.unread = unread;
  },

  UPDATE_GROUP_NAME(state, { group_id, name }) {
    const group = state.groups.find(g => g.id === group_id);
    if (group) group.name = name;
  },

  UPDATE_LAST_READ(state) {
    const group = state.groups.find(g => g.id === state.selectedId);
    if (group) group.last_read_at = new Date().toISOString();
  }
};

const actions = {
  connect,
  disconnect() {
    ws.close(3333);
  },

  deleteMessage(
    { commit, dispatch, state },
    { group_id, message_id, true_delete }
  ) {
    commit("DELETE_MESSAGE", { group_id, message_id, true_delete });

    const group = state.groups.find(g => g.id === group_id);
    if (group.last_message.id === message_id) {
      dispatch("getChatGroups");
    }
  },

  async getAttachmentConfig({ commit, state }) {
    if (state.attachmentConfig.loaded) return;
    const { results } = await API.getChatAttachmentConfig();
    commit("SET_ATTACHMENT_CONFIG", results);
  },

  async getChatGroups({ commit }) {
    const { results } = await API.listChatGroups();
    commit("SET_GROUPS", results);
  },

  async getMessages({ commit, state }) {
    if (state.loading) return false;
    const n = state.totalMessages;
    if (n > 0 && state.messages.length >= n) return false;

    commit("SET_LOADING", true);

    const res = await API.listChatMessages(state.selectedId, state.page);
    if (!res.success) {
      commit("SET_LOADING", false);
      return false;
    }

    const messages = res.results.messages.results.concat(state.messages);
    const totalMessages = res.results.messages.total;

    commit("SET_MESSAGES", messages);
    commit("SET_TOTAL_MESSAGES", totalMessages);
    commit("SET_PAGE", state.page + 1);

    commit("SET_LOADING", false);

    return true;
  },

  async getNumUnread({ commit }) {
    const res = await API.numChatUnread();
    if (!res.success) return;

    commit("SET_UNREAD", res.results);
  },

  reset({ commit }) {
    commit("RESET");
  },

  selectGroup({ commit, dispatch }, id) {
    commit("SET_PAGE", 0);
    commit("SET_MESSAGES", []);
    commit("SET_TOTAL_MESSAGES", 0);
    commit("SET_SELECTED_ID", id);
    dispatch("getMessages");
  },

  updateLastRead({ commit }) {
    commit("UPDATE_LAST_READ");
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
